/** The colour meaning of a status, shared by every page. Rendered by StatusBadge and StatusDot. */
export type StatusTone = 'neutral' | 'success' | 'warning' | 'info' | 'danger';

/**
 * What each tone means, so the same kind of status gets the same colour everywhere:
 * - neutral: not started, draft, archived, nothing to do
 * - info: in progress, queued, a type label (Procedure)
 * - warning: needs someone's attention or a decision, partly done, changed
 * - success: done, agreed, live, matched
 * - danger: failed, error, over or under in a way that's wrong
 */
export const STATUS_TONES: readonly StatusTone[] = ['neutral', 'info', 'warning', 'success', 'danger'];

/** A status value's label and tone. Pages keep a `Record<Status, StatusMeta>` for their own statuses. */
export interface StatusMeta {
  label: string;
  tone: StatusTone;
}

const dotClass: Record<StatusTone, string> = {
  neutral: 'bg-muted-foreground/40',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  danger: 'bg-danger',
};

const textClass: Record<StatusTone, string> = {
  neutral: 'text-muted-foreground',
  success: 'text-success-strong',
  warning: 'text-warning-strong',
  info: 'text-info-strong',
  danger: 'text-danger-strong',
};

/** Class names for a tone, for the rare place a badge or dot doesn't fit (a coloured count). */
export function statusToneClasses(tone: StatusTone) {
  return { dot: dotClass[tone], text: textClass[tone] };
}

/**
 * A coloured left edge for rows and cards whose status should read at a glance (the factory's runs and
 * machines). Rows get an inset bar on their first cell, plus a soft tint for warning and danger; cards get
 * a 4px left border. Keep it for things in motion or in trouble: calm states (done, cancelled) go without.
 */
const rowAccentClass: Record<StatusTone, string> = {
  neutral: '[&>td:first-child]:shadow-[inset_3px_0_0_var(--muted-foreground)]',
  success: '[&>td:first-child]:shadow-[inset_3px_0_0_var(--success)]',
  info: '[&>td:first-child]:shadow-[inset_3px_0_0_var(--info)]',
  warning: 'bg-warning-soft/60 [&>td:first-child]:shadow-[inset_3px_0_0_var(--warning)]',
  danger: 'bg-danger-soft/60 [&>td:first-child]:shadow-[inset_3px_0_0_var(--danger)]',
};

const listAccentClass: Record<StatusTone, string> = {
  neutral: 'shadow-[inset_3px_0_0_var(--muted-foreground)]',
  success: 'shadow-[inset_3px_0_0_var(--success)]',
  info: 'shadow-[inset_3px_0_0_var(--info)]',
  warning: 'bg-warning-soft/60 shadow-[inset_3px_0_0_var(--warning)]',
  danger: 'bg-danger-soft/60 shadow-[inset_3px_0_0_var(--danger)]',
};

const cardAccentClass: Record<StatusTone, string> = {
  neutral: 'border-l-4 border-l-muted-foreground/40',
  success: 'border-l-4 border-l-success',
  info: 'border-l-4 border-l-info',
  warning: 'border-l-4 border-l-warning',
  danger: 'border-l-4 border-l-danger',
};

/** Class names for a status accent: `row` for a DataTable row (getRowClassName), `item` for a list item, `card` for a PageSection. */
export function statusToneAccent(tone: StatusTone) {
  return { row: rowAccentClass[tone], item: listAccentClass[tone], card: cardAccentClass[tone] };
}
